import type { RcraCharacteristic } from "./rcraPictograms";

/**
 * The 40 CFR 261.31/.32/.33 listed-waste hazard codes, mapped to the RCRA
 * characteristics they name. The 261.30(b) letters collapse to four
 * characteristics: I -> Ignitable, C -> Corrosive, R -> Reactive, and
 * E/H/T -> Toxic (the label has one toxicity symbol, the skull). P codes are
 * acute hazardous (H) per 261.33(e); U codes are toxic (T) unless otherwise
 * designated per 261.33(f), so a U code listed only (I), (R) or (C,R) takes
 * just those letters and no Toxic.
 *
 * U378 and U480 are in our canonical code list but not in the current 261.33,
 * so the CFR assigns them no letter. They take Toxic: the default 261.33(f)
 * already sets for every other U code, and what the compliance reviewer asked
 * for where EPA has not specified otherwise.
 *
 * U239 (xylene) is listed (I,T) in one 261.33(f) row and (I) in its other
 * three. It takes the stricter (I,T), as the compliance reviewer asked, until
 * EPA resolves the conflict. U161 (methyl isobutyl ketone) is the same case:
 * 261.33(f) lists it in two (I) rows and one "Pentanol, 4-methyl-" row with no
 * letter, which the table comment reads as listed for toxicity.
 */
export const LISTED_WASTE_HAZARDS: Readonly<Record<string, readonly RcraCharacteristic[]>> =
  Object.freeze({
    F001: ["Toxic"],
    F002: ["Toxic"],
    F003: ["Ignitable"],
    F004: ["Toxic"],
    F005: ["Ignitable", "Toxic"],
    F006: ["Toxic"],
    F007: ["Reactive", "Toxic"],
    F008: ["Reactive", "Toxic"],
    F009: ["Reactive", "Toxic"],
    F010: ["Reactive", "Toxic"],
    F011: ["Reactive", "Toxic"],
    F012: ["Toxic"],
    F019: ["Toxic"],
    F020: ["Toxic"],
    F021: ["Toxic"],
    F022: ["Toxic"],
    F023: ["Toxic"],
    F024: ["Toxic"],
    F025: ["Toxic"],
    F026: ["Toxic"],
    F027: ["Toxic"],
    F028: ["Toxic"],
    F032: ["Toxic"],
    F034: ["Toxic"],
    F035: ["Toxic"],
    F037: ["Toxic"],
    F038: ["Toxic"],
    F039: ["Toxic"],
    K001: ["Toxic"],
    K002: ["Toxic"],
    K003: ["Toxic"],
    K004: ["Toxic"],
    K005: ["Toxic"],
    K006: ["Toxic"],
    K007: ["Toxic"],
    K008: ["Toxic"],
    K009: ["Toxic"],
    K010: ["Toxic"],
    K011: ["Reactive", "Toxic"],
    K013: ["Reactive", "Toxic"],
    K014: ["Toxic"],
    K015: ["Toxic"],
    K016: ["Toxic"],
    K017: ["Toxic"],
    K018: ["Toxic"],
    K019: ["Toxic"],
    K020: ["Toxic"],
    K021: ["Toxic"],
    K022: ["Toxic"],
    K023: ["Toxic"],
    K024: ["Toxic"],
    K025: ["Toxic"],
    K026: ["Toxic"],
    K027: ["Reactive", "Toxic"],
    K028: ["Toxic"],
    K029: ["Toxic"],
    K030: ["Toxic"],
    K031: ["Toxic"],
    K032: ["Toxic"],
    K033: ["Toxic"],
    K034: ["Toxic"],
    K035: ["Toxic"],
    K036: ["Toxic"],
    K037: ["Toxic"],
    K038: ["Toxic"],
    K039: ["Toxic"],
    K040: ["Toxic"],
    K041: ["Toxic"],
    K042: ["Toxic"],
    K043: ["Toxic"],
    K044: ["Reactive"],
    K045: ["Reactive"],
    K046: ["Toxic"],
    K047: ["Reactive"],
    K048: ["Toxic"],
    K049: ["Toxic"],
    K050: ["Toxic"],
    K051: ["Toxic"],
    K052: ["Toxic"],
    K060: ["Toxic"],
    K061: ["Toxic"],
    K062: ["Corrosive", "Toxic"],
    K069: ["Toxic"],
    K071: ["Toxic"],
    K073: ["Toxic"],
    K083: ["Toxic"],
    K084: ["Toxic"],
    K085: ["Toxic"],
    K086: ["Toxic"],
    K087: ["Toxic"],
    K088: ["Toxic"],
    K093: ["Toxic"],
    K094: ["Toxic"],
    K095: ["Toxic"],
    K096: ["Toxic"],
    K097: ["Toxic"],
    K098: ["Toxic"],
    K099: ["Toxic"],
    K100: ["Toxic"],
    K101: ["Toxic"],
    K102: ["Toxic"],
    K103: ["Toxic"],
    K104: ["Toxic"],
    K105: ["Toxic"],
    K106: ["Toxic"],
    K107: ["Corrosive", "Toxic"],
    K108: ["Ignitable", "Toxic"],
    K109: ["Toxic"],
    K110: ["Toxic"],
    K111: ["Corrosive", "Toxic"],
    K112: ["Toxic"],
    K113: ["Toxic"],
    K114: ["Toxic"],
    K115: ["Toxic"],
    K116: ["Toxic"],
    K117: ["Toxic"],
    K118: ["Toxic"],
    K123: ["Toxic"],
    K124: ["Corrosive", "Toxic"],
    K125: ["Toxic"],
    K126: ["Toxic"],
    K131: ["Corrosive", "Toxic"],
    K132: ["Toxic"],
    K136: ["Toxic"],
    K141: ["Toxic"],
    K142: ["Toxic"],
    K143: ["Toxic"],
    K144: ["Toxic"],
    K145: ["Toxic"],
    K147: ["Toxic"],
    K148: ["Toxic"],
    K149: ["Toxic"],
    K150: ["Toxic"],
    K151: ["Toxic"],
    K156: ["Toxic"],
    K157: ["Toxic"],
    K158: ["Toxic"],
    K159: ["Toxic"],
    K161: ["Reactive", "Toxic"],
    K169: ["Toxic"],
    K170: ["Toxic"],
    K171: ["Ignitable", "Toxic"],
    K172: ["Ignitable", "Toxic"],
    K174: ["Toxic"],
    K175: ["Toxic"],
    K176: ["Toxic"],
    K177: ["Toxic"],
    K178: ["Toxic"],
    K181: ["Toxic"],
    P001: ["Toxic"],
    P002: ["Toxic"],
    P003: ["Toxic"],
    P004: ["Toxic"],
    P005: ["Toxic"],
    P006: ["Reactive", "Toxic"],
    P007: ["Toxic"],
    P008: ["Toxic"],
    P009: ["Reactive", "Toxic"],
    P010: ["Toxic"],
    P011: ["Toxic"],
    P012: ["Toxic"],
    P013: ["Toxic"],
    P014: ["Toxic"],
    P015: ["Toxic"],
    P016: ["Toxic"],
    P017: ["Toxic"],
    P018: ["Toxic"],
    P020: ["Toxic"],
    P021: ["Toxic"],
    P022: ["Toxic"],
    P023: ["Toxic"],
    P024: ["Toxic"],
    P026: ["Toxic"],
    P027: ["Toxic"],
    P028: ["Toxic"],
    P029: ["Toxic"],
    P030: ["Toxic"],
    P031: ["Toxic"],
    P033: ["Toxic"],
    P034: ["Toxic"],
    P036: ["Toxic"],
    P037: ["Toxic"],
    P038: ["Toxic"],
    P039: ["Toxic"],
    P040: ["Toxic"],
    P041: ["Toxic"],
    P042: ["Toxic"],
    P043: ["Toxic"],
    P044: ["Toxic"],
    P045: ["Toxic"],
    P046: ["Toxic"],
    P047: ["Toxic"],
    P048: ["Toxic"],
    P049: ["Toxic"],
    P050: ["Toxic"],
    P051: ["Toxic"],
    P054: ["Toxic"],
    P056: ["Toxic"],
    P057: ["Toxic"],
    P058: ["Toxic"],
    P059: ["Toxic"],
    P060: ["Toxic"],
    P062: ["Toxic"],
    P063: ["Toxic"],
    P064: ["Toxic"],
    P065: ["Reactive", "Toxic"],
    P066: ["Toxic"],
    P067: ["Toxic"],
    P068: ["Toxic"],
    P069: ["Toxic"],
    P070: ["Toxic"],
    P071: ["Toxic"],
    P072: ["Toxic"],
    P073: ["Toxic"],
    P074: ["Toxic"],
    P075: ["Toxic"],
    P076: ["Toxic"],
    P077: ["Toxic"],
    P078: ["Toxic"],
    P081: ["Reactive", "Toxic"],
    P082: ["Toxic"],
    P084: ["Toxic"],
    P085: ["Toxic"],
    P087: ["Toxic"],
    P088: ["Toxic"],
    P089: ["Toxic"],
    P092: ["Toxic"],
    P093: ["Toxic"],
    P094: ["Toxic"],
    P095: ["Toxic"],
    P096: ["Toxic"],
    P097: ["Toxic"],
    P098: ["Toxic"],
    P099: ["Toxic"],
    P101: ["Toxic"],
    P102: ["Toxic"],
    P103: ["Toxic"],
    P104: ["Toxic"],
    P105: ["Toxic"],
    P106: ["Toxic"],
    P108: ["Toxic"],
    P109: ["Toxic"],
    P110: ["Toxic"],
    P111: ["Toxic"],
    P112: ["Reactive", "Toxic"],
    P113: ["Toxic"],
    P114: ["Toxic"],
    P115: ["Toxic"],
    P116: ["Toxic"],
    P118: ["Toxic"],
    P119: ["Toxic"],
    P120: ["Toxic"],
    P121: ["Toxic"],
    P122: ["Reactive", "Toxic"],
    P123: ["Toxic"],
    P127: ["Toxic"],
    P128: ["Toxic"],
    P185: ["Toxic"],
    P188: ["Toxic"],
    P189: ["Toxic"],
    P190: ["Toxic"],
    P191: ["Toxic"],
    P192: ["Toxic"],
    P194: ["Toxic"],
    P196: ["Toxic"],
    P197: ["Toxic"],
    P198: ["Toxic"],
    P199: ["Toxic"],
    P201: ["Toxic"],
    P202: ["Toxic"],
    P203: ["Toxic"],
    P204: ["Toxic"],
    P205: ["Toxic"],
    U001: ["Ignitable"],
    U002: ["Ignitable"],
    U003: ["Ignitable", "Toxic"],
    U004: ["Toxic"],
    U005: ["Toxic"],
    U006: ["Corrosive", "Reactive", "Toxic"],
    U007: ["Toxic"],
    U008: ["Ignitable"],
    U009: ["Toxic"],
    U010: ["Toxic"],
    U011: ["Toxic"],
    U012: ["Ignitable", "Toxic"],
    U014: ["Toxic"],
    U015: ["Toxic"],
    U016: ["Toxic"],
    U017: ["Toxic"],
    U018: ["Toxic"],
    U019: ["Ignitable", "Toxic"],
    U020: ["Corrosive", "Reactive"],
    U021: ["Toxic"],
    U022: ["Toxic"],
    U023: ["Corrosive", "Reactive", "Toxic"],
    U024: ["Toxic"],
    U025: ["Toxic"],
    U026: ["Toxic"],
    U027: ["Toxic"],
    U028: ["Toxic"],
    U029: ["Toxic"],
    U030: ["Toxic"],
    U031: ["Ignitable"],
    U032: ["Toxic"],
    U033: ["Reactive", "Toxic"],
    U034: ["Toxic"],
    U035: ["Toxic"],
    U036: ["Toxic"],
    U037: ["Toxic"],
    U038: ["Toxic"],
    U039: ["Toxic"],
    U041: ["Toxic"],
    U042: ["Toxic"],
    U043: ["Toxic"],
    U044: ["Toxic"],
    U045: ["Ignitable", "Toxic"],
    U046: ["Toxic"],
    U047: ["Toxic"],
    U048: ["Toxic"],
    U049: ["Toxic"],
    U050: ["Toxic"],
    U051: ["Toxic"],
    U052: ["Toxic"],
    U053: ["Toxic"],
    U055: ["Ignitable"],
    U056: ["Ignitable"],
    U057: ["Ignitable"],
    U058: ["Toxic"],
    U059: ["Toxic"],
    U060: ["Toxic"],
    U061: ["Toxic"],
    U062: ["Toxic"],
    U063: ["Toxic"],
    U064: ["Toxic"],
    U066: ["Toxic"],
    U067: ["Toxic"],
    U068: ["Toxic"],
    U069: ["Toxic"],
    U070: ["Toxic"],
    U071: ["Toxic"],
    U072: ["Toxic"],
    U073: ["Toxic"],
    U074: ["Ignitable", "Toxic"],
    U075: ["Toxic"],
    U076: ["Toxic"],
    U077: ["Toxic"],
    U078: ["Toxic"],
    U079: ["Toxic"],
    U080: ["Toxic"],
    U081: ["Toxic"],
    U082: ["Toxic"],
    U083: ["Toxic"],
    U084: ["Toxic"],
    U085: ["Ignitable", "Toxic"],
    U086: ["Toxic"],
    U087: ["Toxic"],
    U088: ["Toxic"],
    U089: ["Toxic"],
    U090: ["Toxic"],
    U091: ["Toxic"],
    U092: ["Ignitable"],
    U093: ["Toxic"],
    U094: ["Toxic"],
    U095: ["Toxic"],
    U096: ["Reactive"],
    U097: ["Toxic"],
    U098: ["Toxic"],
    U099: ["Toxic"],
    U101: ["Toxic"],
    U102: ["Toxic"],
    U103: ["Toxic"],
    U105: ["Toxic"],
    U106: ["Toxic"],
    U107: ["Toxic"],
    U108: ["Toxic"],
    U109: ["Toxic"],
    U110: ["Ignitable"],
    U111: ["Toxic"],
    U112: ["Ignitable"],
    U113: ["Ignitable"],
    U114: ["Toxic"],
    U115: ["Ignitable", "Toxic"],
    U116: ["Toxic"],
    U117: ["Ignitable"],
    U118: ["Toxic"],
    U119: ["Toxic"],
    U120: ["Toxic"],
    U121: ["Toxic"],
    U122: ["Toxic"],
    U123: ["Corrosive", "Toxic"],
    U124: ["Ignitable"],
    U125: ["Ignitable"],
    U126: ["Toxic"],
    U127: ["Toxic"],
    U128: ["Toxic"],
    U129: ["Toxic"],
    U130: ["Toxic"],
    U131: ["Toxic"],
    U132: ["Toxic"],
    U133: ["Reactive", "Toxic"],
    U134: ["Corrosive", "Toxic"],
    U135: ["Toxic"],
    U136: ["Toxic"],
    U137: ["Toxic"],
    U138: ["Toxic"],
    U140: ["Ignitable", "Toxic"],
    U141: ["Toxic"],
    U142: ["Toxic"],
    U143: ["Toxic"],
    U144: ["Toxic"],
    U145: ["Toxic"],
    U146: ["Toxic"],
    U147: ["Toxic"],
    U148: ["Toxic"],
    U149: ["Toxic"],
    U150: ["Toxic"],
    U151: ["Toxic"],
    U152: ["Ignitable", "Toxic"],
    U153: ["Ignitable", "Toxic"],
    U154: ["Ignitable"],
    U155: ["Toxic"],
    U156: ["Ignitable", "Toxic"],
    U157: ["Toxic"],
    U158: ["Toxic"],
    U159: ["Ignitable", "Toxic"],
    U160: ["Reactive", "Toxic"],
    U161: ["Ignitable", "Toxic"],
    U162: ["Ignitable", "Toxic"],
    U163: ["Toxic"],
    U164: ["Toxic"],
    U165: ["Toxic"],
    U166: ["Toxic"],
    U167: ["Toxic"],
    U168: ["Toxic"],
    U169: ["Ignitable", "Toxic"],
    U170: ["Toxic"],
    U171: ["Ignitable", "Toxic"],
    U172: ["Toxic"],
    U173: ["Toxic"],
    U174: ["Toxic"],
    U176: ["Toxic"],
    U177: ["Toxic"],
    U178: ["Toxic"],
    U179: ["Toxic"],
    U180: ["Toxic"],
    U181: ["Toxic"],
    U182: ["Toxic"],
    U183: ["Toxic"],
    U184: ["Toxic"],
    U185: ["Toxic"],
    U186: ["Ignitable"],
    U187: ["Toxic"],
    U188: ["Toxic"],
    U189: ["Reactive"],
    U190: ["Toxic"],
    U191: ["Toxic"],
    U192: ["Toxic"],
    U193: ["Toxic"],
    U194: ["Ignitable", "Toxic"],
    U196: ["Toxic"],
    U197: ["Toxic"],
    U200: ["Toxic"],
    U201: ["Toxic"],
    U203: ["Toxic"],
    U204: ["Toxic"],
    U205: ["Reactive", "Toxic"],
    U206: ["Toxic"],
    U207: ["Toxic"],
    U208: ["Toxic"],
    U209: ["Toxic"],
    U210: ["Toxic"],
    U211: ["Toxic"],
    U213: ["Ignitable"],
    U214: ["Toxic"],
    U215: ["Toxic"],
    U216: ["Toxic"],
    U217: ["Toxic"],
    U218: ["Toxic"],
    U219: ["Toxic"],
    U220: ["Toxic"],
    U221: ["Toxic"],
    U222: ["Toxic"],
    U223: ["Reactive", "Toxic"],
    U225: ["Toxic"],
    U226: ["Toxic"],
    U227: ["Toxic"],
    U228: ["Toxic"],
    U234: ["Reactive", "Toxic"],
    U235: ["Toxic"],
    U236: ["Toxic"],
    U237: ["Toxic"],
    U238: ["Toxic"],
    U239: ["Ignitable", "Toxic"],
    U240: ["Toxic"],
    U243: ["Toxic"],
    U244: ["Toxic"],
    U246: ["Toxic"],
    U247: ["Toxic"],
    U248: ["Toxic"],
    U249: ["Toxic"],
    U271: ["Toxic"],
    U278: ["Toxic"],
    U279: ["Toxic"],
    U280: ["Toxic"],
    U328: ["Toxic"],
    U353: ["Toxic"],
    U359: ["Toxic"],
    U364: ["Toxic"],
    U367: ["Toxic"],
    U372: ["Toxic"],
    U373: ["Toxic"],
    U378: ["Toxic"],
    U387: ["Toxic"],
    U389: ["Toxic"],
    U394: ["Toxic"],
    U395: ["Toxic"],
    U404: ["Toxic"],
    U409: ["Toxic"],
    U410: ["Toxic"],
    U411: ["Toxic"],
    U480: ["Toxic"],
  });

/** The RCRA characteristics a listed waste code (F/K/P/U) names, or an empty
 * array for a code the table does not carry -- an unrecognised code contributes
 * nothing rather than a guessed hazard. */
export function listedWasteHazardsFor(code: string): readonly RcraCharacteristic[] {
  // The canonical code list writes one entry as "U480*". The asterisk is a
  // footnote marker, not part of the code.
  const normalised = code.trim().toUpperCase().replace(/\*+$/, "");
  return LISTED_WASTE_HAZARDS[normalised] ?? [];
}
